import { AdminLayout } from "@/components/admin-layout";
import { listOpportunities } from "@fp/admin-api-client";
import { useGetOpportunityClaims } from "@fp/admin-api-client/client";
import { useGetOpportunityShares } from "@fp/admin-api-client/client";
import type { OpportunityDto } from "@fp/admin-api-client/types/opportunityDto.js";
import { PayRateType } from "@fp/admin-api-client/types/payRateType.js";
import { OpportunityShareType } from "@fp/admin-api-client/types/opportunityShareType.js";
import { AdminPageHeader } from "@/components/admin-page-header";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { FilterButton } from "@fp/ui/components/opportunities/filter-button";
import { sortOpportunities } from "@fp/ui/components/opportunities/utils";
import { SortButton } from "@fp/ui/components/opportunities/sort-button";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { User01 } from "@untitledui/icons";
import { useFilterableData } from "@/hooks/useFilterableData";

type OppCategory = "all" | "drafts" | "shared" | "claimed" | "closed";

const OppCategoryLabels: Record<OppCategory, string> = {
	all: "All",
	drafts: "Drafts",
	shared: "Shared",
	claimed: "Claimed",
	closed: "Closed",
};

const getStatusForCategory = (category: OppCategory): string | undefined => {
	switch (category) {
		case "all":
			return undefined;
		case "drafts":
			return "draft";
		case "shared":
			return "shared";
		case "claimed":
			return "claimed";
		case "closed":
			return "closed";
		default:
			return undefined;
	}
};

export const Route = createFileRoute("/opps/categories/$category")({
	component: RouteComponent,
	loader: async ({ context: { queryClient }, params }) => {
		const category = params.category as OppCategory;
		const status = getStatusForCategory(category);
		const opportunities = await queryClient.fetchQuery({
			queryKey: ["opportunities", "category", category],
			queryFn: () =>
				listOpportunities({
					page: 1,
					pageSize: 100,
					status,
				}),
		});
		return {
			category,
			items: opportunities?.data?.items ?? [],
		};
	},
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const { category, items: initialOpportunities } = Route.useLoaderData();
	const navigate = useNavigate();
	const status = getStatusForCategory(category as OppCategory);

	const {
		data: filteredAndSortedOpportunities,
		isLoading,
		handleApplyFilters,
		sort,
		setSort,
	} = useFilterableData({
		initialData: initialOpportunities,
		queryFn: listOpportunities,
		queryKeyPrefix: ["opportunities", "category", category, "filtered"],
		baseQueryParams: { page: 1, pageSize: 100, status },
		sortFn: sortOpportunities,
	});

	const onItemSelect = (oppId: string) => {
		navigate({
			to: `/opps/$oppId`,
			params: { oppId },
		});
	};

	if (isLoading) {
		return <PageLoading />;
	}

	return (
		<AdminLayout>
			<AdminPageHeader
				title={OppCategoryLabels[category as OppCategory]}
				parent={{ url: "/opps", label: "Opps" }}
			/>
			<PageContentContainer className="p-0">
				<div className="p-4">
					<div className="flex justify-end gap-2">
						<FilterButton onApplyFilters={handleApplyFilters} />
						<SortButton value={sort} onChange={setSort} />
					</div>
				</div>

				<OpportunityList
					items={filteredAndSortedOpportunities}
					category={category as OppCategory}
					onItemSelect={onItemSelect}
				/>
			</PageContentContainer>
		</AdminLayout>
	);
}

type OpportunityListItemProps = {
	item: OpportunityDto;
	category: OppCategory;
	onSelect: (opportunityId: string) => void;
};

const OpportunityListItem = ({
	item,
	category,
	onSelect,
}: OpportunityListItemProps) => {
	const handleClick = () => {
		onSelect(item.id);
	};

	return (
		<button
			type="button"
			className="flex flex-col text-left border-b border-gray-200 px-4 py-3 bg-white hover:bg-gray-50 transition-colors w-full"
			onClick={handleClick}
		>
			<div className="flex flex-row items-start justify-between mb-1">
				<h3 className="text-base font-semibold text-brand-600 flex-1 pr-4">
					{item.title}
				</h3>
				<div className="text-base font-semibold text-brand-600 whitespace-nowrap">
					{item.payRate?.type !== PayRateType.Percentage && "$"}
					{item.payRate?.value}
					{item.payRate?.type === PayRateType.Percentage && "%"}
				</div>
			</div>

			{category === "drafts" && <DraftExtraInfo opportunityId={item.id} />}
			{category === "claimed" && <ClaimedExtraInfo opportunityId={item.id} />}
		</button>
	);
};

const DraftExtraInfo = ({ opportunityId }: { opportunityId: string }) => {
	const { data: shares } = useGetOpportunityShares(opportunityId);
	const invitedTalentMembers =
		shares?.data?.items?.filter(
			(share) => share.shareType === OpportunityShareType.User,
		).length || 0;

	return (
		<div className="text-sm text-gray-600">
			Invited talent members - {invitedTalentMembers}
		</div>
	);
};

const ClaimedExtraInfo = ({ opportunityId }: { opportunityId: string }) => {
	const { data: claims } = useGetOpportunityClaims(opportunityId);

	if (!claims?.data.items || claims.data.items.length === 0) {
		return null;
	}

	return (
		<div className="flex items-center text-gray-600 text-xs">
			<User01 className="h-4 w-4 mr-1" />
			Claimed by{" "}
			{claims.data.items.length === 1
				? claims.data.items[0].claimerName
				: claims.data.items.length}
		</div>
	);
};

type OpportunityListProps = {
	items: OpportunityDto[];
	category: OppCategory;
	onItemSelect: (opportunityId: string) => void;
};

const OpportunityList = ({
	items,
	category,
	onItemSelect,
}: OpportunityListProps) => {
	return (
		<div className="flex flex-col bg-white">
			{items.length === 0 ? (
				<div className="text-center py-8 text-gray-500">
					No opportunities found
				</div>
			) : (
				items.map((item) => (
					<OpportunityListItem
						key={item.id}
						item={item}
						category={category}
						onSelect={onItemSelect}
					/>
				))
			)}
		</div>
	);
};